import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Button, Textarea } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { updatePhoneCall } from '@/data/client';
import { OUTCOMES } from '@/components/prospecting/ProspectBits';
import { FormError } from '@/components/crm/Modal';
import { usePhone } from './PhoneProvider';
import type { PhoneCall } from '@/data/types';

/* The top-bar phone: an Available / Away toggle, the incoming-call banner, the
   live call (who, timer, mute, hang up), and the outcome prompt after. Renders
   nothing when browser calling is not configured. */
export function Softphone() {
  const p = usePhone();
  if (!p.enabled) return null;
  return (
    <>
      <button type="button" className={`sp-toggle ${p.available ? 'is-on' : ''} ${p.state === 'off' ? 'is-off' : ''}`}
        title={p.state === 'off' ? 'Starting the phone…' : p.available ? 'Inbound calls ring here. Click to go away.' : 'Not ringing. Click to take calls.'}
        onClick={() => p.setAvailable(!p.available)} disabled={p.state === 'off'}>
        <Icon name="phone" size={14} /> {p.available ? 'Available' : 'Away'}
      </button>
      {p.error && <span className="sp-error" title={p.error}><Icon name="alert-circle" size={14} /></span>}
      {(p.state === 'ringing' || p.state === 'connecting' || p.state === 'in_call') && <CallBar />}
      {p.ended && <OutcomePrompt call={p.ended} onDone={p.dismissEnded} />}
    </>
  );
}

function CallBar() {
  const p = usePhone();
  const link = accountLink(p.current);
  return (
    <div className={`sp-bar sp-bar--${p.state}`} role="status">
      <div className="sp-bar__who">
        <Icon name="phone" size={16} />
        <div>
          <strong>{p.state === 'ringing' ? 'Incoming call' : p.state === 'connecting' ? 'Calling…' : 'On a call'}</strong>
          <span>{link ? <Link to={link}>{p.currentLabel}</Link> : p.currentLabel}{p.current?.otherParty && p.current.who ? ` · ${p.current.otherParty}` : ''}</span>
        </div>
      </div>
      <div className="sp-bar__actions">
        {p.state === 'in_call' && <Timer since={p.startedAt} />}
        {p.state === 'ringing' && <>
          <Button size="sm" onClick={p.answer} iconLeft={<Icon name="phone" size={14} />}>Answer</Button>
          <Button size="sm" variant="outline" onClick={p.reject}>Decline</Button>
        </>}
        {p.state === 'in_call' && <Button size="sm" variant={p.muted ? 'primary' : 'outline'} onClick={p.toggleMute}>{p.muted ? 'Unmute' : 'Mute'}</Button>}
        {p.state !== 'ringing' && <Button size="sm" variant="danger" onClick={p.hangUp} iconLeft={<Icon name="x" size={14} />}>Hang up</Button>}
      </div>
    </div>
  );
}

function Timer({ since }: { since: number | null }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(t); }, []);
  if (!since) return null;
  const s = Math.max(0, Math.floor((now - since) / 1000));
  return <span className="sp-timer">{Math.floor(s / 60)}:{String(s % 60).padStart(2, '0')}</span>;
}

export const accountLink = (c: PhoneCall | null): string | null =>
  c?.dealId ? `/deals/${c.dealId}` : c?.companyId ? `/companies/${c.companyId}` : c?.prospectId ? `/prospecting/${c.prospectId}` : c?.contactId ? `/contacts/${c.contactId}` : null;

/* After the call: a prospect gets the cold-call outcomes (which move its
   rotation, like logging by hand); everyone else gets a note. */
function OutcomePrompt({ call, onDone }: { call: PhoneCall; onDone: () => void }) {
  const qc = useQueryClient();
  const [outcome, setOutcome] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [callbackAt, setCallbackAt] = useState('');
  const save = useMutation({
    mutationFn: () => updatePhoneCall(call.id, { outcome: outcome ?? undefined, note: note || undefined, callbackAt: callbackAt ? new Date(callbackAt).toISOString() : undefined }),
    onSuccess: () => { for (const k of ['phoneCalls', 'prospect', 'prospects', 'prospectSummary', 'timeline', 'company', 'deal']) void qc.invalidateQueries({ queryKey: [k] }); onDone(); },
  });
  const link = accountLink(call);
  const answered = call.status === 'completed';
  return (
    <div className="sp-outcome" role="dialog" aria-label="How did the call go?">
      <div className="sp-outcome__head">
        <strong>{answered ? 'Call ended' : call.direction === 'inbound' ? 'Missed call' : 'No answer'} · {call.who ?? call.otherParty}</strong>
        {link && <Link to={link}>Open account</Link>}
      </div>
      {call.prospectId && (
        <div className="pq-outcomes" role="group" aria-label="How did the call go?">
          {OUTCOMES.phone.map((o) => (
            <button key={o.value} type="button" title={o.hint} className={`pq-outcome ${outcome === o.value ? 'is-on' : ''} pq-outcome--${o.value}`} onClick={() => setOutcome(o.value)}>{o.label}</button>
          ))}
        </div>
      )}
      {outcome === 'callback' && <input className="tr-input" type="datetime-local" aria-label="Call back at" value={callbackAt} onChange={(e) => setCallbackAt(e.target.value)} />}
      <Textarea rows={2} placeholder={call.prospectId ? 'Who you spoke to, what they said…' : 'What was discussed, what is next…'} value={note} onChange={(e) => setNote(e.target.value)} />
      <div className="sp-outcome__actions">
        <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending || (outcome === 'callback' && !callbackAt) || (!!call.prospectId && !outcome && !note)}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>Skip</Button>
      </div>
      <FormError error={save.error} />
    </div>
  );
}
